import React, { useEffect, useRef, useState } from 'react';

import { format as dfFormat } from 'date-fns';
import dayjs from 'dayjs';
import DatePicker from 'react-datepicker';

import 'react-datepicker/dist/react-datepicker.css';
import './DateRangePickerCalendar.css';
import { useDateFnsLocale } from '@shared/hooks/useDateFnsLocale';
import { NavigationIcon } from '@shared/ui/DateRangePickerCalendar/ui/Icons/NavigationIcon';

interface IDateRangePickerCalendarProps {
    value?: [Date | null, Date | null];
    minDate?: Date | null;
    maxDate?: Date | null;
    monthsShown?: number;
    openToDate?: Date;
    onChange?: (range: [Date | null, Date | null]) => void;
}

export const DateRangePickerCalendar = ({
    value = [null, null],
    minDate,
    maxDate,
    monthsShown = 2,
    openToDate,
    onChange,
}: IDateRangePickerCalendarProps) => {
    const [startDate, endDate] = value;

    const currentLocale = useDateFnsLocale();

    const today = dayjs().startOf('day').toDate();
    const defaultMinDate = dayjs('2020-01-01').startOf('day').toDate();
    const safeMaxDate = maxDate ?? today;
    const maxMonthStart = new Date(safeMaxDate.getFullYear(), safeMaxDate.getMonth(), 1);

    const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

    const fallbackOpenToDate =
        openToDate ?? startDate ?? dayjs().subtract(1, 'month').startOf('month').toDate();

    const internalChangeRef = useRef(false);
    const prevRangeRef = useRef<[Date | null, Date | null]>([null, null]);
    const [pickerKey, setPickerKey] = useState(0);

    useEffect(() => {
        const [prevStart, prevEnd] = prevRangeRef.current;
        const changed =
            (prevStart?.getTime() || 0) !== (startDate?.getTime() || 0) ||
            (prevEnd?.getTime() || 0) !== (endDate?.getTime() || 0);

        if (changed) {
            if (!internalChangeRef.current) {
                setPickerKey((k) => k + 1);
            }
            internalChangeRef.current = false;
            prevRangeRef.current = [startDate, endDate];
        }
    }, [startDate, endDate]);

    return (
        <DatePicker
            key={pickerKey}
            selectsRange
            startDate={startDate}
            endDate={endDate}
            onChange={(update: [Date | null, Date | null]) => {
                internalChangeRef.current = true;
                onChange?.(update);
            }}
            showPreviousMonths
            monthsShown={monthsShown}
            locale={currentLocale}
            minDate={minDate ?? defaultMinDate}
            maxDate={safeMaxDate}
            dateFormat="dd.MM.yyyy"
            inline
            openToDate={fallbackOpenToDate}
            renderCustomHeader={({
                monthDate,
                customHeaderCount,
                decreaseMonth,
                increaseMonth,
                prevMonthButtonDisabled,
                nextMonthButtonDisabled,
            }) => {
                const showPrev = customHeaderCount === 0;
                const isRightHeader = customHeaderCount === monthsShown - 1;

                const nextRightMonthStart = new Date(
                    monthDate.getFullYear(),
                    monthDate.getMonth() + 1,
                    1
                );
                const blockNext = nextRightMonthStart.getTime() > maxMonthStart.getTime();
                const showNext = isRightHeader && !blockNext;

                const title = cap(dfFormat(monthDate, 'LLLL yyyy', { locale: currentLocale }));

                return (
                    <div
                        className="react-datepicker__header"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        <button
                            aria-label="Previous Month"
                            className="react-datepicker__navigation react-datepicker__navigation--previous"
                            onClick={decreaseMonth}
                            disabled={prevMonthButtonDisabled}
                            style={{ visibility: showPrev ? 'visible' : 'hidden' }}
                            type="button"
                        >
                            <NavigationIcon type="prev" />
                        </button>

                        <span
                            className="react-datepicker__current-month"
                            style={{ flex: 1, textAlign: 'center', fontWeight: 600 }}
                        >
                            {title}
                        </span>

                        <button
                            aria-label="Next Month"
                            className="react-datepicker__navigation react-datepicker__navigation--next"
                            onClick={increaseMonth}
                            disabled={nextMonthButtonDisabled || !showNext}
                            style={{ visibility: showNext ? 'visible' : 'hidden' }}
                            type="button"
                        >
                            <NavigationIcon type="next" />
                        </button>
                    </div>
                );
            }}
        />
    );
};
